import type { FlagManager } from '@/state/flags/useFlag';
import type { Flag } from '@/state/flags/flag-types';
import { Button } from '@/components/ui/button';
// The mounted Toaster's own sonner: the dashboard's direct 'sonner' is another version, whose
// toasts that Toaster never sees.
import { toast } from '@/components/ui/sonner';
import { UrlTargeting } from '../../Experiments/Details/ExperimentSettings/UrlTargeting';
import { AttributeRulesEditor } from '../../Experiments/Details/ExperimentSettings/ServerSideConfig/AttributeRulesEditor';
import { useRuleDraft } from '../../Experiments/Details/ExperimentSettings/ServerSideConfig/use-rule-draft';
import { FlagSection } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagTargetingProps = { manager: FlagManager; flag: Flag };

const COPY = FLAG_COPY.targeting;
const SERVER_COPY = FLAG_COPY.serverRules;

/**
 * Who can get the flag at all, decided before rings and the rollout: the experiments'
 * Targeting & conditions card for pages, and (FlagServerRules) for a code flag the server SDK's
 * attribute rules.
 * The targeting endpoints are shared with experiments and do not republish, so a live flag
 * gets a Publish rules button; the server rules republish on save.
 */
export const FlagTargeting = ({ manager, flag }: FlagTargetingProps) => {
  const publishRules = async () => {
    if (await manager.publishRules()) toast.success(COPY.published);
  };

  return (
    <div data-tour="flag-targeting">
      <UrlTargeting
        experimentId={flag.id}
        note={[manager.isEditor ? COPY.noteEditor : COPY.noteCode, manager.isLive ? COPY.liveNote : '']
          .filter(Boolean)
          .join(' ')}
        headerAction={
          // Publishing ships the rules to visitors, which takes experiments:start.
          manager.isLive &&
          manager.canChange && (
            <Button
              type="button"
              variant="outline"
              onClick={publishRules}
              disabled={manager.pending === 'rules'}
              // The Saved targeting trigger's size, which sits beside it.
              className="border-input h-10 rounded-lg bg-transparent"
            >
              {COPY.publish}
            </Button>
          )
        }
      />
    </div>
  );
};

/** A code flag's attribute rules, saved on each add and remove, as a Server-side experiment's are. */
export const FlagServerRules = ({ manager, flag }: FlagTargetingProps) => {
  const rules = flag.attributeTargeting ?? [];
  // On a live flag these change who gets On, so they take experiments:start (canEditAudience).
  const locked = manager.isArchived || !manager.canEditAudience || manager.pending === 'eligibility';

  const save = async (next: typeof rules) => {
    const saved = await manager.saveEligibility(next);
    if (saved) toast.success(SERVER_COPY.saved);
    return saved;
  };
  const ruleEditor = useRuleDraft({ rules, onAdd: (rule) => save([...rules, rule]) });

  return (
    <FlagSection
      title={SERVER_COPY.title}
      help={SERVER_COPY.help}
      description={SERVER_COPY.description}
      tourAnchor="flag-server-rules"
    >
      <AttributeRulesEditor
        manager={{
          ...ruleEditor,
          rules,
          isSaving: locked,
          removeRule: async (index) => {
            await save(rules.filter((_, idx) => idx !== index));
          },
        }}
        copy={{ ...SERVER_COPY, help: SERVER_COPY.helpLine }}
      />
    </FlagSection>
  );
};
